import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container, Box, Paper, Typography, Grid, Button, Alert, CircularProgress, Tooltip, LinearProgress, FormControl, Select, MenuItem, Chip } from "@mui/material";
import { LayoutDashboard, CheckSquare, Calculator, ShoppingBag, ShoppingCart, Users, Package, FolderKanban, Building, TrendingUp, HardHat, Factory, Scale, Stethoscope, Home, Building2, Clock, Wallet, Mail, ArrowLeftRight, FileSpreadsheet, Globe, MessageCircle, GitBranch, QrCode, MessageSquare, Check, X, RotateCcw, Save, Lock, Sparkles } from "lucide-react";
import TitleBox from "../Shared/TitleBox";
import { MODULE_DEFINITIONS, DEFAULTS, getModules, saveModules, resetModules } from "../../hooks/useModules";
import { fetchModuleConfig } from "../../api/index";

// ── Icon + color map ──────────────────────────────────────────────────────────
const MODULE_META = {
  dashboard: {
    icon: LayoutDashboard,
    color: "#4CAF50"
  },
  todos: {
    icon: CheckSquare,
    color: "#1c810e"
  },
  accounting: {
    icon: Calculator,
    color: "#2196F3"
  },
  pos: {
    icon: ShoppingBag,
    color: "#00897B"
  },
  purchases: {
    icon: ShoppingCart,
    color: "#0288D1"
  },
  customers: {
    icon: Users,
    color: "#9C27B0"
  },
  inventory: {
    icon: Package,
    color: "#00897B"
  },
  projects: {
    icon: FolderKanban,
    color: "#F57C00"
  },
  assets: {
    icon: Building,
    color: "#5C6BC0"
  },
  costCenters: {
    icon: TrendingUp,
    color: "#0097A7"
  },
  construction: {
    icon: HardHat,
    color: "#FF6F00"
  },
  manufacturing: {
    icon: Factory,
    color: "#0F766E"
  },
  lawFirm: {
    icon: Scale,
    color: "#1A237E"
  },
  clinic: {
    icon: Stethoscope,
    color: "#C62828"
  },
  rentals: {
    icon: Home,
    color: "#795548"
  },
  properties: {
    icon: Building2,
    color: "#4E342E"
  },
  attendance: {
    icon: Clock,
    color: "#E91E63"
  },
  payrolls: {
    icon: Wallet,
    color: "#673AB7"
  },
  letters: {
    icon: Mail,
    color: "#0b5b91"
  },
  importExport: {
    icon: ArrowLeftRight,
    color: "#607D8B"
  },
  excel: {
    icon: FileSpreadsheet,
    color: "#00838F"
  },
  website: {
    icon: Globe,
    color: "#00838F"
  },
  chat: {
    icon: MessageCircle,
    color: "#00ACC1"
  },
  workflow: {
    icon: GitBranch,
    color: "#5C6BC0"
  },
  qrCode: {
    icon: QrCode,
    color: "#3F51B5"
  },
  communications: {
    icon: MessageSquare,
    color: "#546E7A"
  }
};

// Maps camelCase module key → snake_case translation key suffix
const MODULE_T_KEY = {
  dashboard: "dashboard",
  todos: "todos",
  accounting: "accounting",
  pos: "pos",
  purchases: "purchases",
  customers: "customers",
  inventory: "inventory",
  projects: "projects",
  assets: "assets",
  costCenters: "cost_centers",
  construction: "construction",
  manufacturing: "manufacturing",
  lawFirm: "law_firm",
  clinic: "clinic",
  rentals: "rentals",
  properties: "properties",
  attendance: "attendance",
  payrolls: "payrolls",
  letters: "letters",
  importExport: "import_export",
  excel: "excel",
  website: "website",
  chat: "chat",
  workflow: "workflow",
  qrCode: "qr_code",
  communications: "communications"
};

const getModuleLabelKey = key => `module_${MODULE_T_KEY[key] || key}`;
const getModuleDescriptionKey = key => `${getModuleLabelKey(key)}_desc`;
const getPresetLabelKey = preset => preset.tKey;
const getPresetDescriptionKey = preset => `${preset.tKey}_desc`;

// ── Presets ───────────────────────────────────────────────────────────────────
const PRESETS = [{
  id: "all",
  tKey: "preset_all_modules",
  emoji: "🔓",
  color: "#455A64",
  keys: Object.keys(DEFAULTS)
}, {
  id: "medical",
  tKey: "preset_medical",
  emoji: "🏥",
  color: "#C62828",
  keys: ["dashboard", "todos", "accounting", "purchases", "customers", "inventory", "clinic", "attendance", "payrolls", "letters", "excel", "website", "chat", "communications"]
}, {
  id: "lawfirm",
  tKey: "preset_lawfirm",
  emoji: "⚖️",
  color: "#1A237E",
  keys: ["dashboard", "todos", "accounting", "purchases", "customers", "lawFirm", "projects", "assets", "attendance", "payrolls", "letters", "excel", "website", "chat", "workflow"]
}, {
  id: "construction",
  tKey: "preset_construction",
  emoji: "🏗️",
  color: "#FF6F00",
  keys: ["dashboard", "todos", "accounting", "purchases", "customers", "inventory", "construction", "projects", "assets", "costCenters", "attendance", "payrolls", "letters", "importExport", "excel", "website", "chat"]
}, {
  id: "retail",
  tKey: "preset_retail",
  emoji: "🛍️",
  color: "#00897B",
  keys: ["dashboard", "todos", "accounting", "pos", "purchases", "customers", "inventory", "excel", "website", "chat", "communications", "qrCode"]
}, {
  id: "realestate",
  tKey: "preset_realestate",
  emoji: "🏠",
  color: "#4E342E",
  keys: ["dashboard", "todos", "accounting", "customers", "rentals", "properties", "attendance", "payrolls", "letters", "excel", "website", "chat", "communications"]
}, {
  id: "accounting_firm",
  tKey: "preset_accounting_firm",
  emoji: "📊",
  color: "#2196F3",
  keys: ["dashboard", "todos", "accounting", "purchases", "customers", "assets", "projects", "costCenters", "attendance", "payrolls", "letters", "importExport", "excel", "website", "chat", "workflow"]
}, {
  id: "restaurant",
  tKey: "preset_restaurant",
  emoji: "🍽️",
  color: "#E65100",
  keys: ["dashboard", "accounting", "pos", "purchases", "inventory", "customers", "attendance", "payrolls", "excel", "website", "chat", "communications", "qrCode"]
}, {
  id: "minimal",
  tKey: "preset_minimal",
  emoji: "✂️",
  color: "#546E7A",
  keys: ["dashboard", "accounting", "customers", "excel"]
}];

// Build a full config object from a preset's key list
const configFromPreset = keys => Object.keys(DEFAULTS).reduce((acc, k) => ({
  ...acc,
  [k]: keys.includes(k)
}), {});

// Detect which preset the current config matches (or "custom")
const detectPreset = config => {
  for (const p of PRESETS) {
    const pc = configFromPreset(p.keys);
    if (Object.keys(pc).every(k => pc[k] === config[k])) return p.id;
  }
  return "custom";
};

// ── Helper ────────────────────────────────────────────────────────────────────
const isAdminUser = () => {
  try {
    const p = JSON.parse(localStorage.getItem("profile"));
    return p?.result?.groupId === "admin" || p?.result?.userType === "admin";
  } catch {
    return false;
  }
};

// ── Module card ───────────────────────────────────────────────────────────────
function ModuleCard({
  moduleKey,
  label,
  description,
  enabled,
  onToggle,
  isAdmin
}) {
  const {
    t
  } = useTranslation("admin");
  const meta = MODULE_META[moduleKey] || {
    icon: Package,
    color: "#607D8B"
  };
  const Icon = meta.icon;
  const color = meta.color;
  return <Tooltip title={isAdmin ? enabled ? t("click_to_disable") : t("click_to_enable") : t("view_only")} arrow>
      <Paper elevation={0} onClick={() => isAdmin && onToggle(moduleKey)} sx={{
      p: 2.5,
      borderRadius: 2.5,
      border: "1.5px solid",
      borderColor: enabled ? `${color}55` : "rgba(0,0,0,0.08)",
      bgcolor: enabled ? `${color}08` : "#fafafa",
      cursor: isAdmin ? "pointer" : "default",
      transition: "all 0.18s ease",
      position: "relative",
      overflow: "hidden",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 1.25,
      userSelect: "none",
      "&:hover": isAdmin ? {
        borderColor: enabled ? `${color}88` : "rgba(0,0,0,0.18)",
        transform: "translateY(-2px)",
        boxShadow: `0 4px 16px ${enabled ? color + "22" : "rgba(0,0,0,0.07)"}`
      } : {}
    }}>
        <Box sx={{
        position: "absolute",
        top: 10,
        right: 10,
        width: 22,
        height: 22,
        borderRadius: "50%",
        bgcolor: enabled ? color : "rgba(0,0,0,0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 0.18s"
      }}>
          {enabled ? <Check size={12} color="#fff" strokeWidth={3} /> : <X size={12} color="#fff" strokeWidth={2.5} />}
        </Box>

        <Box sx={{
        width: 44,
        height: 44,
        borderRadius: 2,
        bgcolor: enabled ? `${color}18` : "rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background-color 0.18s"
      }}>
          <Icon size={22} color={enabled ? color : "#aaa"} strokeWidth={1.75} />
        </Box>

        <Box>
          <Typography variant="body2" fontWeight={700} sx={{
          color: enabled ? "#0f172a" : "#94a3b8",
          lineHeight: 1.3
        }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{
          color: enabled ? "#64748b" : "#b0bec5",
          lineHeight: 1.5,
          display: "block",
          mt: 0.4
        }}>
            {description}
          </Typography>
        </Box>
      </Paper>
    </Tooltip>;
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function ModuleSettings() {
  const {
    t
  } = useTranslation("admin");
  const [config, setConfig] = useState(getModules);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const isAdmin = isAdminUser();
  useEffect(() => {
    fetchModuleConfig().then(r => {
      const remote = r.data;
      if (remote && typeof remote === "object" && Object.keys(remote).length > 0) {
        setConfig({
          ...DEFAULTS,
          ...remote
        });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const currentPreset = useMemo(() => detectPreset(config), [config]);
  const handlePresetChange = async presetId => {
    if (!isAdmin || presetId === "custom") return;
    const preset = PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    const newConfig = configFromPreset(preset.keys);
    setConfig(newConfig);
    setSaving(true);
    setError("");
    try {
      await saveModules(newConfig);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.status === 403 ? t("Only administrators can change module configuration.") : e.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };
  const toggle = key => {
    if (!isAdmin) return;
    setConfig(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setSaved(false);
  };
  const handleSave = async () => {
    if (!isAdmin) return;
    setSaving(true);
    setError("");
    try {
      await saveModules(config);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.status === 403 ? t("only_admins_can_change_modules") : e.response?.data?.message || t("failed_to_save") || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };
  const handleReset = async () => {
    if (!isAdmin) return;
    if (!window.confirm(t("reset_all_modules_confirm"))) return;
    setSaving(true);
    setError("");
    try {
      await resetModules();
      setConfig({
        ...DEFAULTS
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.message || t("failed_to_reset"));
    } finally {
      setSaving(false);
    }
  };
  const allKeys = MODULE_DEFINITIONS.flatMap(g => g.items.map(i => i.key));
  const enabledCount = allKeys.filter(k => config[k]).length;
  const total = allKeys.length;
  const pct = Math.round(enabledCount / total * 100);
  const activePreset = PRESETS.find(p => p.id === currentPreset);
  if (loading) {
    return <Container maxWidth={false} className="AppContainer-root">
        <Box sx={{
        display: "flex",
        justifyContent: "center",
        py: 12
      }}><CircularProgress /></Box>
      </Container>;
  }
  return <Container maxWidth={false} className="AppContainer-root">
      <TitleBox title={t("module_configuration")} subTitle={t("module_configuration_subtitle")} allowPrint={false} />

      {!isAdmin && <Alert severity="info" icon={<Lock size={16} />} sx={{
      mb: 3,
      borderRadius: 2
    }}>
          {t("module_view_only_notice")}
        </Alert>}
      {error && <Alert severity="error" sx={{
      mb: 2,
      borderRadius: 2
    }} onClose={() => setError("")}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{
      mb: 2,
      borderRadius: 2
    }} onClose={() => setSaved(false)}>{t("module_config_saved")}</Alert>}

      {/* ── Preset selector ───────────────────────────────────────────── */}
      <Paper elevation={0} sx={{
      mb: 3,
      borderRadius: 3,
      border: "1px solid",
      borderColor: activePreset && currentPreset !== "custom" ? `${activePreset.color}44` : "divider",
      overflow: "hidden",
      background: activePreset && currentPreset !== "custom" ? `linear-gradient(135deg, ${activePreset.color}08 0%, ${activePreset.color}04 100%)` : "linear-gradient(135deg,#f8fafc 0%,#f1f5f9 100%)",
      transition: "all 0.25s ease"
    }}>
        <Box sx={{
        px: 3,
        py: 2.5,
        display: "flex",
        alignItems: "center",
        gap: 2.5,
        flexWrap: "wrap"
      }}>
          <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
            <Sparkles size={18} color="#F57C00" />
            <Typography variant="subtitle2" fontWeight={700} sx={{
            color: "#0f172a",
            whiteSpace: "nowrap"
          }}>
              {t("activity_preset")}
            </Typography>
          </Box>

          <FormControl size="small" sx={{
          minWidth: 260
        }}>
            <Select value={currentPreset} onChange={e => handlePresetChange(e.target.value)} disabled={!isAdmin} displayEmpty renderValue={val => {
            if (val === "custom") return <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 1
            }}>
                    <span>✏️</span>
                    <Typography variant="body2" fontWeight={600}>{t("custom_configuration")}</Typography>
                  </Box>;
            const p = PRESETS.find(p => p.id === val);
            return p ? <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 1
            }}>
                    <span>{p.emoji}</span>
                    <Typography variant="body2" fontWeight={600}>{t(getPresetLabelKey(p))}</Typography>
                  </Box> : null;
          }} sx={{
            borderRadius: 2,
            bgcolor: "#fff",
            fontWeight: 600
          }}>
              {PRESETS.map(p => <MenuItem key={p.id} value={p.id}>
                  <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 0.25,
                width: "100%"
              }}>
                    <Typography sx={{
                  fontSize: "1.2rem",
                  lineHeight: 1
                }}>{p.emoji}</Typography>
                    <Box sx={{
                  flexGrow: 1
                }}>
                      <Typography variant="body2" fontWeight={700}>{t(getPresetLabelKey(p))}</Typography>
                      <Typography variant="caption" color="text.secondary">{t(getPresetDescriptionKey(p))}</Typography>
                    </Box>
                    <Chip label={`${p.keys.length} ${t("modules") || "modules"}`} size="small" sx={{
                  fontSize: "0.68rem",
                  height: 20,
                  bgcolor: `${p.color}18`,
                  color: p.color,
                  fontWeight: 700
                }} />
                  </Box>
                </MenuItem>)}
              {currentPreset === "custom" && <MenuItem value="custom" sx={{
              pointerEvents: "none",
              opacity: 0.6
            }}>
                  <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 0.25
              }}>
                    <Typography sx={{
                  fontSize: "1.2rem"
                }}>✏️</Typography>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>{t("custom_configuration")}</Typography>
                      <Typography variant="caption" color="text.secondary">{t("custom_configuration_desc")}</Typography>
                    </Box>
                  </Box>
                </MenuItem>}
            </Select>
          </FormControl>

          {activePreset && currentPreset !== "custom" && <Typography variant="body2" color="text.secondary" sx={{
          flexGrow: 1
        }}>
              {t(getPresetDescriptionKey(activePreset))} — <strong style={{
            color: activePreset.color
          }}>{activePreset.keys.length}</strong> {t("modules_will_be_enabled")}
            </Typography>}
          {currentPreset === "custom" && <Typography variant="body2" color="text.secondary" sx={{
          flexGrow: 1
        }}>
              {t("custom_configuration_hint")}
            </Typography>}
        </Box>
      </Paper>

      {/* ── Stats + actions banner ─────────────────────────────────────── */}
      <Paper elevation={0} sx={{
      mb: 4,
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      overflow: "hidden"
    }}>
        <Box sx={{
        px: 3,
        py: 2.5,
        display: "flex",
        alignItems: "center",
        gap: 3,
        flexWrap: "wrap",
        background: "linear-gradient(135deg,#f8fafc 0%,#f1f5f9 100%)"
      }}>
          <Box sx={{
          display: "flex",
          gap: 3
        }}>
            <Box sx={{
            textAlign: "center"
          }}>
              <Typography variant="h4" fontWeight={800} sx={{
              color: "#16a34a",
              lineHeight: 1
            }}>{enabledCount}</Typography>
              <Typography variant="caption" color="text.secondary">{t("enabled")}</Typography>
            </Box>
            <Box sx={{
            width: "1px",
            bgcolor: "divider"
          }} />
            <Box sx={{
            textAlign: "center"
          }}>
              <Typography variant="h4" fontWeight={800} sx={{
              color: "#94a3b8",
              lineHeight: 1
            }}>{total - enabledCount}</Typography>
              <Typography variant="caption" color="text.secondary">{t("disabled")}</Typography>
            </Box>
            <Box sx={{
            width: "1px",
            bgcolor: "divider"
          }} />
            <Box sx={{
            textAlign: "center"
          }}>
              <Typography variant="h4" fontWeight={800} sx={{
              color: "#0f172a",
              lineHeight: 1
            }}>{total}</Typography>
              <Typography variant="caption" color="text.secondary">{t("total")}</Typography>
            </Box>
          </Box>

          <Box sx={{
          flexGrow: 1,
          minWidth: 160
        }}>
            <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 0.75
          }}>
              <Typography variant="caption" color="text.secondary">{t("active_modules")}</Typography>
              <Typography variant="caption" fontWeight={700} sx={{
              color: "#16a34a"
            }}>{pct}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={pct} sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: "rgba(0,0,0,0.07)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              bgcolor: "#16a34a"
            }
          }} />
          </Box>

          {isAdmin && <Box sx={{
          display: "flex",
          gap: 1.5,
          ml: "auto"
        }}>
              <Button variant="outlined" color="inherit" size="small" startIcon={<RotateCcw size={14} />} onClick={handleReset} disabled={saving} sx={{
            borderRadius: 2
          }}>
                {t("reset_to_defaults")}
              </Button>
              <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={14} />} onClick={handleSave} disabled={saving} sx={{
            borderRadius: 2,
            px: 2.5
          }}>
                {saving ? t("saving") : t("save_changes")}
              </Button>
            </Box>}
        </Box>
      </Paper>

      {/* ── All modules — flat 4-per-row grid ─────────────────────────── */}
      <Grid container spacing={2}>
        {MODULE_DEFINITIONS.flatMap(group => group.items.map(({
        key
      }) => {
        return <Grid item xs={12} sm={6} md={3} lg={2} key={key}>
                <ModuleCard moduleKey={key} label={t(getModuleLabelKey(key))} description={t(getModuleDescriptionKey(key))} enabled={!!config[key]} onToggle={toggle} isAdmin={isAdmin} />
              </Grid>;
      }))}
      </Grid>

      {/* ── Bottom save ────────────────────────────────────────────────── */}
      {isAdmin && <Box sx={{
      display: "flex",
      justifyContent: "flex-end",
      gap: 1.5,
      mt: 5,
      pt: 3,
      borderTop: "1px solid",
      borderColor: "divider"
    }}>
          <Button variant="outlined" color="inherit" startIcon={<RotateCcw size={16} />} onClick={handleReset} disabled={saving} sx={{
        borderRadius: 2
      }}>
            {t("reset_to_defaults")}
          </Button>
          <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />} onClick={handleSave} disabled={saving} sx={{
        px: 4,
        borderRadius: 2
      }}>
            {saving ? t("saving") : t("save_changes")}
          </Button>
        </Box>}
    </Container>;
}
